import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import StickyMobileCTA from '@/components/StickyMobileCTA';
import SEOHead from '@/components/SEOHead';
import FAQSection from '@/components/sections/FAQSection';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle, XCircle, Target, Brain, LineChart, Lightbulb, Compass, Users, Heart, Shield, Clock, BarChart3, Eye, Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import contentImage from '@/assets/content-strategy.jpg';
import teamImage from '@/assets/team-shooting-3.jpg';

const ServiceContentStrategy = () => {
  const painPoints = [
    { problem: "You're creating content but have no idea if it's actually working", solution: "Every piece of content we plan has a measurable goal tied directly to your business outcomes" },
    { problem: "You've wasted money on ads with no clear strategy behind them", solution: "Data-driven campaigns built on audience research—not guesswork or gut feelings" },
    { problem: "You feel overwhelmed by all the platforms and trends to keep up with", solution: "We cut through the noise and focus only on what moves the needle for YOUR business" },
    { problem: "You've hired 'strategists' who gave you a PDF and disappeared", solution: "Ongoing partnership with weekly check-ins, real-time optimization, and hands-on execution" },
  ];

  const included = [
    { icon: Brain, title: 'Deep Audience Research', desc: 'We study your ideal clients—where they hang out, what they care about, and what makes them buy.' },
    { icon: Compass, title: 'Content Roadmap', desc: 'A detailed, month-by-month content plan aligned with your business goals and seasonal opportunities.' },
    { icon: Eye, title: 'Competitor Analysis', desc: 'We analyze what your competitors are doing right (and wrong) so you can leapfrog them.' },
    { icon: Megaphone, title: 'Brand Voice Development', desc: 'A unique, authentic voice that resonates with your audience and sets you apart from everyone else.' },
    { icon: LineChart, title: 'Performance Tracking', desc: 'Real-time dashboards and monthly reports showing exactly how your content is performing.' },
    { icon: Lightbulb, title: 'Ongoing Optimization', desc: 'We don\'t set it and forget it. We continuously test, learn, and improve based on real data.' },
  ];

  const bigAgencyProblems = [
    'Deliver a strategy deck and call it done',
    'Use the same playbook for every industry',
    'Junior analysts who\'ve never run a real business',
    'Vanity metrics that look great but mean nothing',
    'Three-month minimums before you see any ROI',
    'Strategy meetings that could\'ve been an email',
  ];

  const vpmDifference = [
    'Strategy AND execution—we do the work, not just the plan',
    'Industry-specific strategies built from real experience',
    'Senior strategists who understand business, not just marketing',
    'Metrics that matter: leads, conversions, revenue growth',
    'Results within weeks, not months',
    'Efficient communication—your time is never wasted',
  ];

  const strategyProcess = [
    { step: '01', title: 'Discovery & Audit', desc: 'We deep-dive into your brand, audience, competitors, and current content performance.' },
    { step: '02', title: 'Strategy Development', desc: 'We build a custom content roadmap with clear goals, themes, and KPIs.' },
    { step: '03', title: 'Execution & Creation', desc: 'We bring the strategy to life with high-quality content across the right platforms.' },
    { step: '04', title: 'Measure & Optimize', desc: 'We track everything, report on what matters, and continuously refine the approach.' },
  ];

  const faqs = [
    { q: "What is content strategy?", a: "Content strategy is the planning, development, and management of content—written, visual, and video—across all your marketing channels. It determines what to create, why, for whom, and how it fits together to achieve business goals like lead generation, brand awareness, and client acquisition." },
    { q: "How does content strategy work at Vantage Point Media?", a: "We start with a deep-dive audit of your brand, audience, competitors, and current content performance. Then we build a custom content roadmap with clear goals, themes, and KPIs. We execute the strategy with high-quality content, track performance, and continuously optimize based on real data." },
    { q: "Who needs content strategy services?", a: "Content strategy is essential for any professional service business—med spas, dental practices, financial advisors, real estate agents, and surgeons—that wants to stop guessing and start seeing measurable results from their marketing efforts." },
    { q: "How much does content strategy cost in Columbus Ohio?", a: "Content strategy packages in Columbus typically range from $1,500 to $5,000+ per month depending on scope. Vantage Point Media offers custom packages significantly less expensive than hiring an in-house marketing team and more effective than big agencies. Book a call for a transparent quote." },
    { q: "What is the best content marketing agency in Columbus Ohio?", a: "Vantage Point Media is one of the top content strategy agencies in Columbus, Ohio. Unlike traditional consultants who deliver a plan and disappear, we provide strategy AND execution—creating the content, managing distribution, and optimizing performance. Our clients see engagement improvements within weeks." },
    { q: "Isn't content strategy just a fancy word for social media?", a: "Not at all. Content strategy encompasses your entire brand messaging—social media, video, website, ads, email, and more. It's the master plan that makes every piece of content work together toward your business goals. Social media management is one channel within a broader strategy." },
    { q: "Do you actually execute the strategy or just hand us a plan?", a: "Both. We build the strategy AND execute it. Unlike consultants who hand you a binder and wave goodbye, we're in the trenches with you—creating content, managing your social media, producing video, and optimizing in real-time." },
    { q: "How do you measure content strategy success?", a: "We focus on metrics that impact your bottom line—leads generated, conversion rates, engagement quality, and revenue growth. Not vanity metrics like follower counts that look good but don't pay the bills. Monthly reports show exactly how content is driving business outcomes." },
    { q: "What if my industry is too niche for content marketing?", a: "There's no such thing as too niche. In fact, niche industries are often the EASIEST to create standout content for because most competitors are doing nothing. That's your biggest opportunity—and Vantage Point Media specializes in professional service niches." },
    { q: "How soon will I see results from content strategy?", a: "You'll see engagement improvements within weeks. Meaningful lead generation typically kicks in by month 2. By month 3, you'll wonder why you didn't start sooner. Consistency is key—businesses with a clear content strategy see 150-400% increases in profile activity." },
    { q: "What is the best content strategy for med spas?", a: "The best content strategy for med spas combines educational content about treatments, before-and-after showcases, provider spotlights, patient testimonials, and seasonal promotional campaigns. Vantage Point Media pairs strategic planning with professional video production and social media management for maximum impact." },
    { q: "How is content strategy different from social media management?", a: "Content strategy is the high-level plan that determines what to create, why, and how it all connects. Social media management is the day-to-day execution on social platforms. We offer both as standalone or combined services—and they're most powerful together." },
    { q: "Do you offer content strategy near me in Columbus Ohio?", a: "Yes! Vantage Point Media provides content strategy services throughout Columbus, Dublin, Westerville, Worthington, Powell, Hilliard, Grove City, and all of Central Ohio. We work closely with local businesses who want data-driven marketing strategies." },
    { q: "What's the investment for content strategy?", a: "Our strategy packages are customized to your needs and budget. They're significantly less than hiring an in-house marketing team and more effective than the big agencies. Book a discovery call for a transparent, no-pressure quote." },
  ];

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-0">
      <SEOHead
        title="Content Strategy Services Columbus Ohio | Data-Driven Marketing | Vantage Point Media"
        description="Data-driven content strategy in Columbus Ohio. Audience research, content planning, competitor analysis, performance tracking & ongoing optimization for med spas, dentists, financial advisors & professionals."
        canonical="https://vantagepointmediagroup.com/services/content-strategy"
        keywords="content strategy Columbus Ohio, content marketing Columbus, digital marketing strategy Columbus Ohio, content planning Columbus, marketing consultant Columbus Ohio, best content marketing agency Columbus"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Content Strategy Columbus Ohio",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Vantage Point Media",
              "url": "https://vantagepointmediagroup.com",
              "telephone": "+1-614-625-0865",
              "email": "shay@vantagepointmediagroup.com",
              "areaServed": { "@type": "State", "name": "Ohio" }
            },
            "areaServed": { "@type": "State", "name": "Ohio" },
            "description": "Data-driven content strategy with audience research, content planning, competitor analysis, performance tracking, and ongoing optimization for businesses in Columbus Ohio.",
            "url": "https://vantagepointmediagroup.com/services/content-strategy",
            "serviceType": "Content Strategy"
          }
        ]}
      />
      <Header />

      <main>
        {/* Hero */}
        <section className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src={contentImage} alt="Content strategy planning Columbus Ohio" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground/95 via-foreground/90 to-foreground/75" />
          </div>
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 bg-accent/20 backdrop-blur-sm border border-accent/30 rounded-full px-4 py-1.5 mb-6">
                <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                <span className="text-background text-xs sm:text-sm font-medium">Content Strategy</span>
              </span>
              <h1 className="font-display font-extrabold text-3xl sm:text-5xl lg:text-6xl text-background leading-tight mb-6">
                Best Content Strategy
                <span className="block text-primary mt-2">Agency in Columbus, Ohio</span>
              </h1>
              <p className="text-base sm:text-xl text-background/85 max-w-2xl mb-8 leading-relaxed">
                Anyone can post on social media. But turning content into a predictable client-generating machine? That takes strategy. Vantage Point Media builds data-driven content strategies that help med spas, dentists, and professionals generate consistent leads.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="hero" size="xl" className="group" asChild>
                  <a href="/booking">
                    Get Your Free Strategy Session
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Entity Clarity Block */}
        <section className="py-10 sm:py-14 bg-muted/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Vantage Point Media</strong> is a content strategy agency in Columbus, Ohio that helps med spas, dental practices, financial advisors, and professionals grow through data-driven marketing strategies. According to industry trends, businesses with a documented content strategy are 3x more likely to report marketing success—and our clients see meaningful lead generation within 60 days.
              </p>
            </div>
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <h2 className="font-display font-bold text-2xl sm:text-4xl text-foreground mb-4">
                Why Do Businesses Need Content Strategy?
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg">
                If any of these hit close to home, you're not alone—and there's a better way.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {painPoints.map((item, i) => (
                <div key={i} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
                  <div className="flex items-start gap-3 mb-4">
                    <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                    <p className="text-foreground/70 text-sm sm:text-base line-through decoration-destructive/40">{item.problem}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-foreground font-medium text-sm sm:text-base">{item.solution}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-3">What You Get</span>
              <h2 className="font-display font-bold text-2xl sm:text-4xl text-foreground mb-4">
                What's Included in Our Content Strategy?
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg">A strategy that actually gets executed—not just a deck that collects dust.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {included.map((item, i) => (
                <div key={i} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft hover:shadow-medium transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-3">Our Process</span>
              <h2 className="font-display font-bold text-2xl sm:text-4xl text-foreground mb-4">
                How Does Content Strategy Work?
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {strategyProcess.map((item, i) => (
                <div key={i} className="bg-card border border-border rounded-2xl p-6 shadow-soft relative">
                  <div className="absolute -top-3 left-6 bg-accent text-accent-foreground font-display font-bold text-xs px-3 py-0.5 rounded-full">
                    {item.step}
                  </div>
                  <h3 className="font-display font-bold text-lg text-foreground mb-2 mt-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Big Agency vs VPM */}
        <section className="py-16 sm:py-24 bg-foreground text-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <h2 className="font-display font-bold text-2xl sm:text-4xl mb-4">
                Why Choose Vantage Point Media Over Traditional Consultants?
              </h2>
              <p className="text-background/70 text-base sm:text-lg">
                Most "strategists" give you a plan and disappear. We give you a plan AND make it happen.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
              <div className="bg-background/5 border border-background/10 rounded-2xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-destructive/20 rounded-xl flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-destructive" />
                  </div>
                  <h3 className="font-display font-bold text-xl">Traditional Consultants</h3>
                </div>
                <ul className="space-y-4">
                  {bigAgencyProblems.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <XCircle className="w-4 h-4 text-destructive/70 flex-shrink-0 mt-1" />
                      <span className="text-background/70 text-sm sm:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-primary/10 border border-primary/20 rounded-2xl p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-bl-lg">BETTER WAY</div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center">
                    <Heart className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-display font-bold text-xl">Vantage Point Media</h3>
                </div>
                <ul className="space-y-4">
                  {vpmDifference.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                      <span className="text-background/90 text-sm sm:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Partnership */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8 sm:gap-12 items-center max-w-6xl mx-auto">
              <div className="w-full lg:w-1/2">
                <img src={teamImage} alt="Vantage Point Media content strategy session" className="w-full aspect-[4/3] object-cover rounded-2xl shadow-strong" />
              </div>
              <div className="w-full lg:w-1/2">
                <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-3">Real Partnership</span>
                <h2 className="font-display font-bold text-2xl sm:text-3xl text-foreground mb-4">
                  Your Growth Is Personal to Us.
                  <span className="text-primary block mt-1">Not Just Professional.</span>
                </h2>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  We're not a faceless agency. We're a tight-knit team of creatives and strategists who get genuinely excited when your content drives a new lead, a new client, a new milestone. Your growth isn't a KPI to us—it's why we love what we do.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: Users, text: 'A team that celebrates your wins like their own' },
                    { icon: Clock, text: 'Responsive communication—never left on read' },
                    { icon: Shield, text: 'Full transparency in strategy, pricing, and results' },
                    { icon: Heart, text: 'Long-term thinking over quick wins' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-accent" />
                      </div>
                      <span className="text-foreground text-sm sm:text-base">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Authority: Best Content Strategy for Industries */}
        <section className="py-16 sm:py-24 bg-muted/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-foreground mb-6 text-center">
                Best Content Strategy for Professional Services
              </h2>
              <div className="space-y-6">
                <article className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
                  <h3 className="font-display font-semibold text-lg text-foreground mb-3">Best Content Marketing for Med Spas & Aesthetics</h3>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    Med spas with a documented content strategy see significantly higher booking rates and patient retention. The best approach combines educational content about treatments, <Link to="/services/video-production" className="text-primary hover:underline">professional video production</Link> for before-and-afters, and <Link to="/services/social-media" className="text-primary hover:underline">strategic social media management</Link> to build trust and drive appointments.
                  </p>
                </article>
                <article className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
                  <h3 className="font-display font-semibold text-lg text-foreground mb-3">Why Businesses Are Investing in Content Strategy</h3>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    According to industry trends, businesses with a documented content strategy are 3x more likely to report marketing success. Content marketing costs 62% less than traditional marketing while generating 3x more leads. Vantage Point Media helps businesses in Columbus, Ohio develop strategies that deliver measurable ROI.
                  </p>
                </article>
                <article className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
                  <h3 className="font-display font-semibold text-lg text-foreground mb-3">Top Content Solutions for Financial & Professional Services</h3>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    Financial advisors, attorneys, and professional service firms that produce regular thought-leadership content generate significantly more qualified leads. Vantage Point Media creates compliant, authoritative content strategies that position professionals as trusted experts in their fields.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </section>

        {/* Cross-linking */}
        <section className="py-12 sm:py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-display font-bold text-xl sm:text-2xl text-foreground mb-6">
                Explore Our Other Services
              </h2>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/services/social-media" className="bg-card border border-border rounded-xl p-5 shadow-soft hover:shadow-medium transition-all hover:-translate-y-1 flex-1 max-w-xs">
                  <h3 className="font-display font-bold text-foreground mb-1">Social Media Management</h3>
                  <p className="text-muted-foreground text-sm">Full-service social media that grows your brand</p>
                </Link>
                <Link to="/services/video-production" className="bg-card border border-border rounded-xl p-5 shadow-soft hover:shadow-medium transition-all hover:-translate-y-1 flex-1 max-w-xs">
                  <h3 className="font-display font-bold text-foreground mb-1">Video Production</h3>
                  <p className="text-muted-foreground text-sm">Cinematic brand videos that captivate and convert</p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQSection
          faqs={faqs}
          heading="Content Strategy: Questions & Answers"
          subheading="Everything you need to know about content strategy and marketing in Columbus, Ohio."
          pageUrl="https://vantagepointmediagroup.com/services/content-strategy"
        />

        {/* Final CTA */}
        <section className="py-16 sm:py-24 bg-foreground relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display font-bold text-2xl sm:text-4xl text-background mb-4">
              Hope Is Not a Strategy.
              <span className="text-primary block mt-2">Let's Build One That Works.</span>
            </h2>
            <p className="text-background/80 text-base sm:text-lg max-w-2xl mx-auto mb-8">
              Every month without a clear content strategy is a month of wasted potential. Let's turn your content into your most powerful sales tool—starting with a free strategy session.
            </p>
            <Button variant="hero" size="xl" className="group w-full sm:w-auto" asChild>
              <a href="/booking">
                Book Your Free Strategy Session
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
            <p className="mt-4 text-background/50 text-xs sm:text-sm">No contracts. No pressure. Just clarity on your next move.</p>
          </div>
        </section>
      </main>

      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

export default ServiceContentStrategy;
